/**
 * MediaMTX URLs — mirrors DeviceSensor worker MEDIAMTX_PUSH_MODE / MEDIAMTX_PUSH_BASE.
 * Set in zone_editor/.env (REACT_APP_*); restart npm start after changes.
 */

const PUSH_MODE = (
  process.env.REACT_APP_MEDIAMTX_PUSH_MODE || "local"
).toLowerCase();

/** Host for WebRTC/HLS/RTSP playback in local mode */
const LOCAL_PLAYBACK_HOST =
  process.env.REACT_APP_MEDIAMTX_HOST || "localhost";

/** RTMP publish base in local mode (must match worker MEDIAMTX_PUSH_BASE) */
const LOCAL_PUSH_BASE = (
  process.env.REACT_APP_MEDIAMTX_PUSH_BASE || "rtmp://localhost:1935"
)
  .trim()
  .replace(/\/$/, "");

/** Production fallback when API has no rtmPpushLink */
const PRODUCTION_FALLBACK_HOST =
  process.env.REACT_APP_MEDIAMTX_HOST || "158.220.120.141";

export function isLocalMediaMode() {
  return ["local", "dev", "development"].includes(PUSH_MODE);
}

export function getMediaModeLabel() {
  if (isLocalMediaMode()) {
    return `local (${LOCAL_PLAYBACK_HOST})`;
  }
  return "production (API stream links)";
}

export function hostFromRtmPushLink(link) {
  if (!link || typeof link !== "string") return null;
  const trimmed = link.trim();
  if (!trimmed) return null;
  try {
    const normalized = trimmed.replace(/^rtmps?:/i, "http:");
    return new URL(normalized).hostname || null;
  } catch {
    return null;
  }
}

function playbackHost(device) {
  if (isLocalMediaMode()) {
    return LOCAL_PLAYBACK_HOST;
  }
  return (
    hostFromRtmPushLink(device?.rtmPpushLink || device?.RtmPpushLink) ||
    PRODUCTION_FALLBACK_HOST
  );
}

/** Effective RTMP URL the worker publishes to (same rules as worker.py). */
export function getEffectiveRtmpPushUrl(device) {
  if (!device?.id) return "";
  const path = `device${device.id}`;
  if (isLocalMediaMode()) {
    return `${LOCAL_PUSH_BASE}/${path}`;
  }
  const apiLink = device.rtmPpushLink || device.RtmPpushLink;
  if (apiLink && String(apiLink).trim()) {
    return `${String(apiLink).trim().replace(/\/$/, "")}/${path}`;
  }
  return `${LOCAL_PUSH_BASE}/${path}`;
}

/** Restream playback URLs for the dashboard player. */
export function buildStreamUrls(device) {
  if (!device?.id) {
    return { webrtc: "", hls: "", llhls: "", rtsp: "" };
  }

  const path = `device${device.id}`;
  const host = playbackHost(device);

  if (isLocalMediaMode()) {
    return {
      webrtc: `http://${host}:8889/${path}/`,
      hls: `http://${host}:8888/${path}/index.m3u8`,
      llhls: `http://${host}:8888/${path}/index.m3u8?lowLatency=1`,
      rtsp: `rtsp://${host}:8554/${path}`,
    };
  }

  const webrtcApi = device.streamLinkWebRTC || device.StreamLinkWebRTC;
  const hlsApi = device.streamLinkHLS || device.StreamLinkHLS;

  return {
    webrtc:
      (webrtcApi && String(webrtcApi).trim()) ||
      `http://${host}:8889/${path}/`,
    hls:
      (hlsApi && String(hlsApi).trim()) ||
      `http://${host}:8888/${path}/index.m3u8`,
    llhls:
      hlsApi && String(hlsApi).trim()
        ? `${String(hlsApi).trim()}?lowLatency=1`
        : `http://${host}:8888/${path}/index.m3u8?lowLatency=1`,
    rtsp: `rtsp://${host}:8554/${path}`,
  };
}

/**
 * Status API base URL (no trailing slash).
 * - Dev: `/worker-status` → setupProxy.js → localhost:8766
 * - Prod: same-origin `/worker-status` behind nginx (see deploy/nginx-worker-status.conf.example)
 * Do NOT use :8766 in the browser — that port is usually blocked on HTTPS sites.
 */
export const STATUS_API_BASE = (
  process.env.REACT_APP_STATUS_API_URL || "/worker-status"
).replace(/\/$/, "");

const STATUS_FETCH_MS = 4000;

async function statusFetch(path, options = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), STATUS_FETCH_MS);
  try {
    const res = await fetch(`${STATUS_API_BASE}${path}`, {
      ...options,
      signal: controller.signal,
      cache: "no-store",
    });
    if (!res.ok) throw new Error(`Status API ${res.status}`);
    return res;
  } catch (err) {
    if (err.name === "AbortError") {
      throw new Error("Status API timeout (check nginx proxy to port 8766)");
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

export async function fetchWorkerStatus() {
  const res = await statusFetch("/status", { method: "GET" });
  return res.json();
}

export async function requestWorkerReload() {
  await statusFetch("/control/reload", { method: "POST" });
  return { ok: true };
}
